import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface LikeInfo {
  userId: string;
  userName: string;
  avatarUrl?: string;
}

interface ArticleLikes {
  count: number;
  hasLiked: boolean;
  likers: LikeInfo[];
}

export const useKnowledgeBaseLikes = (articleId?: string) => {
  const { user } = useAuth();
  const [likes, setLikes] = useState<ArticleLikes>({
    count: 0,
    hasLiked: false,
    likers: []
  });
  const [loading, setLoading] = useState(true);

  const fetchLikes = useCallback(async () => {
    if (!articleId) {
      setLoading(false);
      return;
    }

    try {
      // Fetch likes with user profiles
      const { data: likesData, error } = await supabase
        .from('team_knowledge_base_likes')
        .select('user_id, created_at')
        .eq('article_id', articleId);

      if (error) throw error;

      const userIds = (likesData || []).map(l => l.user_id);
      
      // Fetch profiles for likers
      let likers: LikeInfo[] = [];
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, first_name, last_name, avatar_url')
          .in('user_id', userIds);

        likers = (profiles || []).map(p => ({
          userId: p.user_id,
          userName: [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unbekannt',
          avatarUrl: p.avatar_url || undefined
        }));
      }

      setLikes({
        count: likesData?.length || 0,
        hasLiked: user ? userIds.includes(user.id) : false,
        likers
      });
    } catch (error) {
      console.error('Error fetching likes:', error);
    } finally {
      setLoading(false);
    }
  }, [articleId, user]);

  const toggleLike = async () => {
    if (!articleId || !user) {
      toast.error('Bitte einloggen um zu liken');
      return;
    }

    try {
      if (likes.hasLiked) {
        // Remove like
        const { error } = await supabase
          .from('team_knowledge_base_likes')
          .delete()
          .eq('article_id', articleId)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Add like
        const { error } = await supabase
          .from('team_knowledge_base_likes')
          .insert({
            article_id: articleId,
            user_id: user.id
          });

        if (error) throw error;
      }

      // Refetch to get updated data
      await fetchLikes();
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  useEffect(() => {
    fetchLikes();
  }, [fetchLikes]);

  // Subscribe to realtime changes
  useEffect(() => {
    if (!articleId) return;

    const channel = supabase
      .channel(`likes-${articleId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'team_knowledge_base_likes',
          filter: `article_id=eq.${articleId}`
        },
        () => {
          fetchLikes();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [articleId, fetchLikes]);

  return {
    ...likes,
    loading,
    toggleLike,
    refresh: fetchLikes
  };
};
